import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Printer {
  id: string;
  name: string;
  manufacturer: string;
  status: 'approved' | 'pending' | 'rejected';
  isPending?: boolean;
}

export interface Manufacturer {
  id: string;
  name: string;
}

export function usePrinterSearch() {
  const { user } = useAuth();
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [loading, setLoading] = useState(false);
  const [manufacturersLoading, setManufacturersLoading] = useState(false);
  const [manufacturersLoaded, setManufacturersLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const ITEMS_PER_PAGE = 50;

  const searchPrinters = async (query: string = '', loadMore: boolean = false) => {
    setLoading(true);
    setError(null);
    
    try {
      const page = loadMore ? currentPage + 1 : 0;
      const from = page * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      let printerQuery = supabase
        .from('printers')
        .select('id_uuid, printer, manufacturer, status')
        .eq('status', 'approved')
        .order('printer')
        .range(from, to);

      if (query.trim()) {
        printerQuery = printerQuery.or(`printer.ilike.%${query}%,manufacturer.ilike.%${query}%`);
      }

      const { data: printerData, error: printerError } = await printerQuery;

      if (printerError) throw printerError;

      const formattedPrinters: Printer[] = (printerData || []).map(printer => ({
        id: printer.id_uuid || '',
        name: printer.printer || '',
        manufacturer: printer.manufacturer || '',
        status: 'approved',
        isPending: false
      }));

      if (loadMore) {
        setPrinters(prev => [...prev, ...formattedPrinters]);
        setCurrentPage(page);
      } else {
        setPrinters(formattedPrinters);
        setCurrentPage(0);
      }

      setHasMore(formattedPrinters.length === ITEMS_PER_PAGE);
    } catch (err) {
      console.error('Error searching printers:', err);
      setError('Failed to search printers');
    } finally {
      setLoading(false);
    }
  };

  const loadPopularPrinters = async () => {
    try {
      // Load most popular printers - using a simple query for now
      const { data, error } = await supabase
        .from('printers')
        .select('id_uuid, printer, manufacturer, status')
        .eq('status', 'approved')
        .limit(20)
        .order('printer');

      if (error) throw error;

      const formattedPrinters: Printer[] = (data || []).map(printer => ({
        id: printer.id_uuid || '',
        name: printer.printer || '',
        manufacturer: printer.manufacturer || '',
        status: 'approved',
        isPending: false
      }));

      // Add popular printers to the beginning of the list
      setPrinters(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newPrinters = formattedPrinters.filter(p => !existingIds.has(p.id));
        return [...newPrinters, ...prev];
      });
    } catch (err) {
      console.error('Error loading popular printers:', err);
    }
  };

  const loadManufacturers = async (searchQuery: string = '') => {
    // Prevent multiple simultaneous calls
    if (manufacturersLoading) {
      console.log('🚫 Manufacturers already loading, skipping...');
      return;
    }

    // If we already have manufacturers and no search query, don't reload
    if (manufacturersLoaded && manufacturers.length > 0 && !searchQuery.trim()) {
      console.log('✅ Manufacturers already loaded, skipping reload');
      return;
    }

    console.log('🔍 Loading manufacturers for printer suggestions with query:', searchQuery);
    setManufacturersLoading(true);
    
    try {
      let query = supabase
        .from('manufacturers')
        .select('id, manufacturer, manufactures_printers')
        .eq('manufactures_printers', true)
        .order('manufacturer');

      // Add search functionality
      if (searchQuery.trim()) {
        query = query.ilike('manufacturer', `%${searchQuery}%`);
      }

      const { data, error } = await query;

      console.log('📊 Printer manufacturers query result:', { data, error, searchQuery });

      if (error) {
        console.error('❌ Error fetching printer manufacturers:', error);
        throw error;
      }

      const formattedManufacturers: Manufacturer[] = (data || []).map((mfg) => ({
        id: mfg.id.toString(),
        name: mfg.manufacturer
      }));

      console.log('✅ Formatted printer manufacturers:', formattedManufacturers);
      setManufacturers(formattedManufacturers);
      setManufacturersLoaded(true);
      
    } catch (err) {
      console.error('💥 Error loading manufacturers:', err);
      setError('Failed to load manufacturers');
    } finally {
      setManufacturersLoading(false);
    }
  };

  const suggestPrinter = async (printerName: string, manufacturerName: string) => {
    if (!user) throw new Error('Authentication required');

    try {
      console.log('🚀 Suggesting printer with user:', user);
      
      // Create the suggestion data
      const suggestionData = {
        name: printerName,
        manufacturer_name: manufacturerName,
        type: 'printer',
        submitted_by: user.id,
        status: 'pending'
      };
      
      console.log('📝 Suggestion data:', suggestionData);

      const { data, error } = await supabase
        .from('printer_manufacturer_suggestions')
        .insert(suggestionData)
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error:', error);
        throw error;
      }

      console.log('✅ Suggestion created successfully:', data);

      // Add the suggested printer to the local state as pending
      const pendingPrinter: Printer = {
        id: `pending-${Date.now()}`,
        name: printerName,
        manufacturer: manufacturerName,
        status: 'pending',
        isPending: true
      };

      setPrinters(prev => [pendingPrinter, ...prev]);
      
      return pendingPrinter;
    } catch (err) {
      console.error('Error suggesting printer:', err);
      throw err;
    }
  };

  const loadMorePrinters = () => {
    if (!loading && hasMore) {
      searchPrinters('', true);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      console.log('🚀 Initializing printer search data...');
      await Promise.all([
        searchPrinters(),
        loadPopularPrinters()
      ]);
      console.log('✅ Initialization complete');
    };

    initializeData();
  }, []);

  return {
    printers,
    manufacturers,
    loading,
    manufacturersLoading,
    error,
    hasMore,
    searchPrinters,
    loadManufacturers,
    suggestPrinter,
    loadMorePrinters
  };
}
